import {ApiError, isAbortError} from '../../errors/ApiError'
import {addBreadcrumb} from '../../errors/breadcrumb'
import {reportError} from '../../errors/reportError'

/** Custom fetch mutator for Orval — prepends /api, attaches Firebase token, retries 5xx/network. */

const MAX_RETRIES = 3
const BASE_DELAY_MS = 300

const wait = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms))

const isRetryableStatus = (status: number) => status >= 500 && status < 600

const parseErrorBody = async (response: Response): Promise<{message: string; details: unknown}> => {
  const body = await response.json().catch(() => null)
  if (body && typeof body === 'object') {
    const message = (body as {message?: string}).message ?? `Request failed: ${response.status}`
    const details = (body as {details?: unknown}).details
    return {message, details}
  }
  return {message: `Request failed: ${response.status}`, details: null}
}

const attachAuthHeader = async (headers: Record<string, string>) => {
  try {
    const {auth} = await import('../../../gui/auth/firebase')
    // Wait for Firebase to finish restoring the session on cold start;
    // otherwise auth.currentUser may still be null and we'd ship an
    // unauthenticated request that 401s and triggers a spurious logout.
    await auth.authStateReady()
    const user = auth.currentUser
    if (user) {
      const token = await user.getIdToken()
      headers['Authorization'] = `Bearer ${token}`
    }
  } catch {
    // Firebase not available (render micro-frontend) — proceed without auth
  }
}

export const customFetch = async <T>(url: string, options: RequestInit): Promise<T> => {
  const prefixedUrl = `/api${url}`
  const method = (options.method ?? 'GET').toUpperCase()

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  }
  await attachAuthHeader(headers)

  let lastNetworkError: unknown = null

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt += 1) {
    const start = performance.now()
    let response: Response
    try {
      response = await fetch(prefixedUrl, {...options, headers})
    } catch (err) {
      if (isAbortError(err)) throw err
      lastNetworkError = err
      const durationMs = Math.round(performance.now() - start)
      addBreadcrumb('http', `${method} ${url}`, 'warning', {
        attempt,
        durationMs,
        error: err instanceof Error ? err.message : String(err),
      })
      if (attempt < MAX_RETRIES && navigator.onLine !== false) {
        await wait(BASE_DELAY_MS * 2 ** attempt)
        continue
      }
      const apiErr = new ApiError(0, navigator.onLine === false ? 'offline' : 'network', null, method, url)
      reportError(apiErr, 'customFetch.network', {underlying: String(err)})
      throw apiErr
    }

    const durationMs = Math.round(performance.now() - start)

    if (response.ok) {
      addBreadcrumb('http', `${method} ${url}`, 'info', {status: response.status, durationMs})
      const responseHeaders = response.headers
      if (response.status === 204) {
        return {data: undefined, status: response.status, headers: responseHeaders} as T
      }
      const data = await response.json()
      return {data, status: response.status, headers: responseHeaders} as T
    }

    if (response.status === 401) {
      addBreadcrumb('auth', `${method} ${url} 401`, 'warning', {durationMs})
      // Only signal session-expired when we actually sent a token. A 401 on an
      // anonymous request means the endpoint requires auth, not that our session died.
      if (headers['Authorization']) {
        window.dispatchEvent(new CustomEvent('app:auth-expired'))
      }
      const {message, details} = await parseErrorBody(response)
      throw new ApiError(401, message, details, method, url)
    }

    if (isRetryableStatus(response.status) && attempt < MAX_RETRIES) {
      addBreadcrumb('http', `${method} ${url}`, 'warning', {
        attempt,
        status: response.status,
        durationMs,
      })
      await wait(BASE_DELAY_MS * 2 ** attempt)
      continue
    }

    const {message, details} = await parseErrorBody(response)
    addBreadcrumb('http', `${method} ${url}`, response.status >= 500 ? 'error' : 'warning', {
      status: response.status,
      durationMs,
      message,
    })
    const apiErr = new ApiError(response.status, message, details, method, url)
    if (response.status >= 500) {
      reportError(apiErr, 'customFetch.5xx')
    }
    throw apiErr
  }

  // Loop fell through — should not happen because every iteration either returns or throws.
  // Treat as network exhaustion for safety.
  const apiErr = new ApiError(0, 'network', null, method, url)
  reportError(apiErr, 'customFetch.unreachable', {lastNetworkError: String(lastNetworkError)})
  throw apiErr
}
